import { TFunction } from "i18next";
import { DefaultValues, useForm } from "react-hook-form";
import { useCase } from "src/context/case-context";
import { AnyObjectSchema, number, object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { OptStatus } from "./case-landing-page";
import countiesList from "../../components/contact-details/address-section/county-list.json";
import { emailRegex, phoneRegex, postcodeRegex, streetRegex } from "src/utils/regex";
import { CheckboxStateValues } from "@ucl/react-library";
import type { CustomerResponse } from "src/api/types/responses";
import { ContactPreference } from "src/api/types/enums";

export type FormSections =
  | "optStatus"
  | "contactDetails"
  | "agreeTsAndCs"
  | "bankDetails"
  | "howToProceed"
  | "receivedLetter"
  | "preferredContactChannel"
  | "challengeDecision";

export enum OfferStatus {
  IntentToObject = "Intent to Object",
  FormalObjection = "Formal Objection",
  Accept = "Accepted",
}

export interface CaseFormTypes {
  optStatus?: OptStatus;
  fullName?: string;
  email?: string;
  homePhone?: string;
  address?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    county?: string;
    postCode?: string;
    country?: string;
    addressSelector?: string;
  };
  preferredContactChannel?: ContactPreference;
  bankDetails?: {
    payeeName?: string;
    sortCode?: string;
    accountNumber?: string;
  };
  agreeTsAndCs?: number;
  howToProceed?: "accept" | "challenge";
  receivedLetter?: "received" | "not-received";
  challengeDecision?: "challenge" | "no-challenge";
  challengeReason?: string;
  challengeExplanation?: string;
  offerStatus?: OfferStatus;
  redress?: {
    docsUploadStatus?: boolean;
  };
}

type SectionConfig = {
  schema: AnyObjectSchema;
  defaultValues: DefaultValues<CaseFormTypes>;
};

export const createAddressSchema = (t: TFunction) =>
  object({
    addressLine1: string()
      .required(t("editContactDetails.errors.address1Required"))
      .matches(streetRegex, t("editContactDetails.errors.address1Required")),

    addressLine2: string().matches(streetRegex, t("editContactDetails.errors.address2Valid")),

    city: string()
      .required(t("editContactDetails.errors.cityRequired"))
      .max(30, t("editContactDetails.errors.cityRequired")),

    county: string(),

    postCode: string()
      .required(t("editContactDetails.errors.postcodeRequired"))
      .matches(postcodeRegex, t("editContactDetails.errors.postcodeRequired")),

    country: string().required(),
  });

export const createContactDetailsSchema = (t: TFunction) =>
  object({
    fullName: string(),

    email: string()
      .required(t("editContactDetails.errors.emailRequired"))
      .matches(emailRegex, t("editContactDetails.errors.emailRequired")),

    homePhone: string()
      .required(t("editContactDetails.errors.phoneNumberRequired"))
      .matches(phoneRegex, t("editContactDetails.errors.phoneNumberRequired")),

    address: createAddressSchema(t),
  });

export const createContactPreferenceSchema = (t: TFunction) =>
  object({
    preferredContactChannel: string().when("optStatus", {
      is: (val: string | undefined) => val === "opt-in" || val === undefined,
      then: (schema) => schema.oneOf([ContactPreference.Email, ContactPreference.Post]).required(t("contactInfo.contactPreferenceRequired")),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

export const getContactDetailsDefaults = (customerData?: CustomerResponse | null): DefaultValues<CaseFormTypes> => {
  const isValidCounty = countiesList.counties.some((option) => option.value === customerData?.address?.county);

  return {
    fullName: customerData?.fullName ?? "",
    email: customerData?.email ?? "",
    homePhone: customerData?.homePhone ?? "",
    address: {
      addressLine1: customerData?.address?.streetName ?? "",
      addressLine2: "",
      city: customerData?.address?.city ?? "",
      county: isValidCounty ? customerData?.address?.county : "",
      postCode: customerData?.address?.postCode ?? "",
      country: customerData?.address?.country ?? "United Kingdom",
      addressSelector: customerData?.address?.streetName ?? "",
    },
  };
};

export const useCaseFormSetup = ({ formSections, t }: { formSections: FormSections[]; t: TFunction }) => {
  const { customerData } = useCase();

  const contactDetailsSchema = createContactDetailsSchema(t);

  const optStatusSchema = object({
    optStatus: string().oneOf(["opt-in", "opt-out"]).required(),
  });

  const agreeTsAndCsSchema = object({
    agreeTsAndCs: number().when("optStatus", {
      is: (val: string | undefined) => val === "opt-in" || val === undefined,
      then: (schema) => schema.oneOf([CheckboxStateValues.CHECKED]).required(),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const howToProceedSchema = object({
    howToProceed: string().oneOf(["accept", "challenge"]).required(),
  });

  const receivedLetterSchema = object({
    receivedLetter: string().oneOf(["received", "not-received"]).required(),
  });

  const bankDetailsSchema = object({
    bankDetails: object({
      payeeName: string().trim().required(t("bankDetailsForm.validation.payeeNameRequired")),

      sortCode: string()
        .required(t("bankDetailsForm.validation.sortCodeRequired"))
        .test(
          "sortCode",
          t("bankDetailsForm.validation.sortCodeRequired"),
          (value) => (value?.replace(/\D/g, "").length ?? 0) === 6
        ),

      accountNumber: string()
        .required(t("bankDetailsForm.validation.accountNumberRequired"))
        .test(
          "accountNumber",
          t("bankDetailsForm.validation.accountNumberRequired"),
          (value) => (value?.replace(/\D/g, "").length ?? 0) === 8
        ),
    }),
  });

  const contactPreferenceSchema = createContactPreferenceSchema(t);

  const challengeDecisionSchema = object({
    challengeDecision: string().oneOf(["challenge", "no-challenge"]).required("Please select an option"),
  });

  const contactDetailsDefaults = getContactDetailsDefaults(customerData);

  const optStatusDefaults: DefaultValues<CaseFormTypes> = {
    optStatus: undefined,
  };

  const agreeTsAndCsDefault: DefaultValues<CaseFormTypes> = {
    agreeTsAndCs: 0,
  };

  const howToProceedDefaults: DefaultValues<CaseFormTypes> = {
    howToProceed: undefined,
  };

  const receivedLetterDefaults: DefaultValues<CaseFormTypes> = {
    receivedLetter: undefined,
  };

  const bankDetailsDefaults: DefaultValues<CaseFormTypes> = {
    bankDetails: {
      payeeName: "",
      sortCode: "",
      accountNumber: "",
    },
  };

  const contactPreferenceDefaults: DefaultValues<CaseFormTypes> = {
    preferredContactChannel: customerData?.preferredContactChannel ?? undefined,
  };

  const challengeDecisionDefaults: DefaultValues<CaseFormTypes> = {
    challengeDecision: undefined,
  };

  const sectionConfigs: Record<FormSections, SectionConfig> = {
    contactDetails: {
      schema: contactDetailsSchema,
      defaultValues: contactDetailsDefaults,
    },
    optStatus: {
      schema: optStatusSchema,
      defaultValues: optStatusDefaults,
    },
    agreeTsAndCs: {
      schema: agreeTsAndCsSchema,
      defaultValues: agreeTsAndCsDefault,
    },
    bankDetails: {
      schema: bankDetailsSchema,
      defaultValues: bankDetailsDefaults,
    },
    howToProceed: {
      schema: howToProceedSchema,
      defaultValues: howToProceedDefaults,
    },
    receivedLetter: {
      schema: receivedLetterSchema,
      defaultValues: receivedLetterDefaults,
    },
    preferredContactChannel: {
      schema: contactPreferenceSchema,
      defaultValues: contactPreferenceDefaults,
    },
    challengeDecision: {
      schema: challengeDecisionSchema,
      defaultValues: challengeDecisionDefaults,
    },
  };

  let combinedSchema: AnyObjectSchema = object({});
  formSections.forEach((section) => {
    combinedSchema = combinedSchema.concat(sectionConfigs[section].schema);
  });

  let combinedDefaults: DefaultValues<CaseFormTypes> = {};
  formSections.forEach((section) => {
    combinedDefaults = {
      ...combinedDefaults,
      ...sectionConfigs[section].defaultValues,
    };
  });

  const methods = useForm<CaseFormTypes>({
    resolver: yupResolver(combinedSchema),
    values: combinedDefaults,
    resetOptions: {
      keepDirtyValues: true,
    },
    mode: "all",
    reValidateMode: "onChange",
  });

  return methods;
};
